import { trackCustomEvent } from '@/hooks/client/use-analytics-engine'
import { EventType } from '@/lib/analytics/events'
import type { TrackingProperties } from '@/lib/analytics/tracking-props'

const observeHeadings = (node: Element, observer: IntersectionObserver, pending: Set<Element>, seen: Set<string>) => {
  const sections = [node.closest('section[aria-labelledby]'), ...node.querySelectorAll('section[aria-labelledby]')]
  for (const section of sections) {
    if (!section?.closest('main') || section.closest('[data-advisor-placement]')) {
      continue
    }
    const heading = section.querySelector<HTMLElement>(
      `[id="${section.getAttribute('aria-labelledby')?.split(' ')[0]}"]`
    )
    if (heading && !pending.has(heading) && !seen.has(heading.id)) {
      pending.add(heading)
      observer.observe(heading)
    }
  }
}

const watchSections = (root: HTMLElement, observer: IntersectionObserver, pending: Set<Element>, seen: Set<string>) => {
  const mutations = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node instanceof Element) {
          observeHeadings(node, observer, pending, seen)
        }
      }
    }
  })
  mutations.observe(root, { childList: true, subtree: true })
  const onVisible = () => {
    if (document.visibilityState === 'visible') {
      for (const heading of pending) {
        observer.unobserve(heading)
        observer.observe(heading)
      }
    }
  }
  document.addEventListener('visibilitychange', onVisible)
  return () => {
    mutations.disconnect()
    document.removeEventListener('visibilitychange', onVisible)
  }
}

// Observe headings rather than whole sections: the directory can be several
// screens tall, so a 50%-of-section threshold would never be reached.
export const observeHubSections = (
  root: HTMLElement,
  onViewed?: (section: string) => void,
  properties: TrackingProperties = {}
) => {
  const seen = new Set<string>()
  const pending = new Set<Element>()
  const observer = new IntersectionObserver(
    (entries) => {
      if (document.visibilityState === 'hidden') {
        return
      }
      for (const entry of entries) {
        const section = entry.target.id
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5 || seen.has(section)) {
          continue
        }
        seen.add(section)
        pending.delete(entry.target)
        onViewed?.(section)
        trackCustomEvent('hub_section_viewed', EventType.Impression, { ...properties, section })
        observer.unobserve(entry.target)
      }
    },
    { threshold: 0.5 }
  )
  observeHeadings(root, observer, pending, seen)
  // The US-only Guide and streamed sections may mount after the shell effect.
  const stopWatching = watchSections(root, observer, pending, seen)
  return () => {
    observer.disconnect()
    stopWatching()
  }
}
